import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import * as Dialog from '@radix-ui/react-dialog';
import Icon from '@/components/Icon';
import { track } from '@/lib/analytics';
import { api } from '@/lib/api';
import { anchorKey, cardSelector, scrollCardIntoView } from '@/lib/grid-anchor';
import { listingWhereAge } from '@/lib/listing-place';
import { useSearch, type ListingCardData } from '@/stores/search';
import { useUi } from '@/stores/ui';

interface GalleryPhoto {
  id: string;
  variants: { variant: string; key: string }[];
}

interface Gallery {
  gallery: GalleryPhoto[];
  media_base: string;
  advertiser_id: string;
  working_name: string;
}

/**
 * Opening the viewer means fetching the advertiser's profile — the only public route that returns
 * a gallery. Reopening the same person should not refetch, and `lib/api.ts` has no cache of its
 * own, so one lives here. It is a per-tab memo of public data with no invalidation: a photo
 * published while somebody is reading appears on their next visit, which is the same freshness
 * the rest of the public surface offers.
 */
const cache = new Map<string, Promise<Gallery>>();

function loadGallery(username: string): Promise<Gallery> {
  const existing = cache.get(username);
  if (existing) return existing;
  const request = api<{
    gallery: GalleryPhoto[];
    media_base: string;
    profile: { advertiser_id: string; working_name: string };
  }>(`/p/${encodeURIComponent(username)}`).then((data) => ({
    gallery: data.gallery,
    media_base: data.media_base,
    advertiser_id: data.profile.advertiser_id,
    working_name: data.profile.working_name,
  }));
  cache.set(username, request);
  // A failure must not be remembered, or a flaky first open is permanent for the tab.
  request.catch(() => cache.delete(username));
  return request;
}

/**
 * The profile page already holds the gallery, so opening the viewer from it should not ask the
 * server for the same profile a second time. The page hands over what it loaded.
 */
export function seedGallery(username: string, gallery: Gallery): void {
  cache.set(username, Promise.resolve(gallery));
}

/** The marker on the history entry the viewer pushes, so Back closes it rather than the page. */
const VIEWER_ENTRY = 'eeViewer';

function onViewerEntry(): boolean {
  const state = history.state as Record<string, unknown> | null;
  return Boolean(state && state[VIEWER_ENTRY]);
}

/** Below this a drag is a tap. Above it, the reader meant to move. */
const SWIPE_PX = 48;

/**
 * A tap is a pointer that barely moved, in either axis. Between this and `SWIPE_PX` the gesture
 * is ambiguous and does nothing: guessing wrong shows a photograph nobody asked for, where a
 * short swipe that misses costs the reader one repeat.
 */
const TAP_PX = 12;

/** Past this the pixels are bigger than the detail, so the zoom stops being worth anything. */
const MAX_SCALE = 4;

/**
 * How far a step slides in before it waits, as a share of the stage, when the next photograph has
 * not finished downloading. Far enough that the placeholder saying whose picture is coming can be
 * read, short of the whole way, so the slide visibly finishes the moment the picture lands.
 */
const PEEK = 0.35;

/** The brand's move duration (`$duration-move`), for a slide driven from script. */
const SLIDE_MS = 200;

/**
 * Photographs already downloaded and decoded, keyed by the `srcset` that asked for them. A step
 * whose photograph is in here slides straight through; one that is not slides part way onto a
 * placeholder and waits. Per tab and never invalidated: a decoded photograph stays usable.
 */
const ready = new Set<string>();

/** A step in progress: which way, to which position, and how far through it is. */
interface Slide {
  dir: 1 | -1;
  target: number;
  /** The incoming photograph's `srcset`, empty when it has none to wait for. */
  set: string;
  /** `drag` follows a finger; `going` is under way; `waiting` is parked on the placeholder. */
  phase: 'drag' | 'going' | 'waiting' | 'finishing' | 'back';
}

/**
 * The photographs, full screen, and nothing else.
 *
 * A visitor who taps a picture wants the picture bigger; every fact about the advertiser is one
 * tap away on the profile behind it. Anything else here would be a second profile page competing
 * with the real one.
 */
export default function PhotoViewer() {
  const viewer = useUi((state) => state.viewer);
  const closeViewer = useUi((state) => state.closeViewer);
  const setIndex = useUi((state) => state.setViewerIndex);

  // The feed's own cards, which are what a viewer opened from one steps through.
  const listings = useSearch((state) => state.listings);
  const mediaBase = useSearch((state) => state.mediaBase);

  const [data, setData] = useState<Gallery | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  // The gallery is only fetched for a viewer that is showing one, so stepping through the feed
  // costs no requests at all: every card already carries the picture it needs.
  const username = viewer && viewer.source === 'gallery' ? viewer.username : null;

  useEffect(() => {
    setFailed(false);
    if (!username) {
      setData(null);
      return;
    }
    let live = true;
    void loadGallery(username).then(
      (loaded) => {
        if (live) setData(loaded);
      },
      () => {
        if (!live) return;
        setData(null);
        setFailed(true);
      },
    );
    return () => {
      live = false;
    };
  }, [username, attempt]);

  /*
    The viewer is a screen of its own to the reader, so Back closes it. On a phone that is the
    gesture everybody reaches for, and without an entry of its own it left the page underneath
    while the photograph stayed open over wherever the reader landed.

    The entry is pushed outside the router, carrying the router's own state, so the router sees
    the same location on either side of it: nothing re-renders, no key changes, and the grid's
    card anchor — which is keyed on the history entry — is not disturbed. Closing by any other
    route (the close button, Escape) steps back off the entry, so it never lingers.
  */
  const open = viewer !== null;
  useEffect(() => {
    if (!open) return;
    if (!onViewerEntry()) history.pushState({ ...(history.state ?? {}), [VIEWER_ENTRY]: true }, '');
    const onPop = () => {
      if (!onViewerEntry()) useUi.getState().closeViewer();
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [open]);

  const close = () => {
    if (onViewerEntry()) history.back();
    else closeViewer();
  };

  /*
    The plate's words are the person, so they go to the profile; the rest of the screen still
    steps. The profile takes the viewer's own history entry rather than stacking on it, so Back
    from the profile is the grid, not an empty step that used to hold a viewer — and the grid
    comes back to the advertiser the reader was looking at, which is rarely the tile they opened.
    A modified click opens a new tab and leaves everything here as it is.
  */
  const openProfile = (event: React.MouseEvent, at: ListingCardData) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    const replace = onViewerEntry();
    useSearch.getState().setAnchor(at.listing_id, anchorKey(location));
    closeViewer();
    navigate(`/p/${at.username}`, { replace });
  };

  // A navigation of the router's own — a link followed from under the viewer — leaves it behind.
  const location = useLocation();
  const locationKey = location.key;
  const navigate = useNavigate();
  const firstKey = useRef(locationKey);
  useEffect(() => {
    if (firstKey.current === locationKey) return;
    firstKey.current = locationKey;
    useUi.getState().closeViewer();
  }, [locationKey]);

  const imgRef = useRef<HTMLImageElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  /**
   * The photograph being replaced, painted behind the stage until its successor has decoded, so a
   * step never flashes an empty screen on a slow connection.
   */
  const [behind, setBehind] = useState<string | null>(null);

  /*
    The slide. The stage holds a track; the track holds the photograph on screen and, during a
    step, the incoming one beside it. A finger drags the track; a release, a tap, a key or a step
    button animates it the rest of the way. The offset is a ref and is written straight onto the
    track, for the same reason the zoom is: a re-render per pointer event would drop frames.
  */
  const trackRef = useRef<HTMLDivElement>(null);
  const incomingRef = useRef<HTMLImageElement>(null);
  const offset = useRef(0);
  const [slide, setSlideState] = useState<Slide | null>(null);
  const slideRef = useRef<Slide | null>(null);
  const setSlide = (next: Slide | null) => {
    slideRef.current = next;
    setSlideState(next);
  };
  // Re-renders when a photograph becomes ready, so a placeholder is swapped for its picture.
  const [, setReadyTick] = useState(0);
  /** The latest completion check, for photographs that finish downloading mid-slide. */
  const onReady = useRef<(set: string) => void>(() => {});
  const markReady = (set: string) => {
    if (!set || ready.has(set)) return;
    ready.add(set);
    setReadyTick((n) => n + 1);
    onReady.current(set);
  };

  // A viewer closed mid-step opens next time at rest.
  useEffect(() => {
    if (open) return;
    slideRef.current = null;
    setSlideState(null);
    offset.current = 0;
  }, [open]);

  /**
   * The live transform, and the gesture producing it. Refs, not state: a pinch or a pan writes
   * these on every pointer event, and a re-render per frame would drop the photograph below the
   * frame rate the gesture is judged by. Only `zoomed` is state, because only the cursor and the
   * label read it.
   */
  const view = useRef({ scale: 1, x: 0, y: 0 });
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef<{
    startX: number;
    startY: number;
    lastX: number;
    lastY: number;
    /** Set at the first touch: a zoomed photograph is moved, an unzoomed one is left. */
    panning: boolean;
    /** Set once an unzoomed drag is plainly sideways: the track follows the finger from then on. */
    sliding: boolean;
    /** Non-zero once a second finger lands: the distance the pinch began at. */
    pinchFrom: number;
    scaleFrom: number;
  } | null>(null);
  const [zoomed, setZoomed] = useState(false);

  // A photograph is handed over at its own size. Carrying a zoom across would frame the next one
  // on whatever corner of the last one happened to be under the reader's thumb.
  useEffect(() => {
    view.current = { scale: 1, x: 0, y: 0 };
    if (imgRef.current) imgRef.current.style.transform = '';
    setZoomed(false);
  }, [viewer?.index, username]);

  /** The latest `zoomAt`, for the native wheel listener, which is bound once per open. */
  const zoomRef = useRef<((scale: number, x?: number, y?: number) => void) | null>(null);

  // Ctrl-wheel and a trackpad pinch (which arrives as a ctrl-wheel) zoom at the cursor. A native
  // listener, because React's wheel listener is passive and could not stop the page zooming too.
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const onWheel = (event: WheelEvent) => {
      if (!event.ctrlKey) return;
      event.preventDefault();
      zoomRef.current?.(view.current.scale * Math.exp(-event.deltaY * 0.01), event.clientX, event.clientY);
    };
    stage.addEventListener('wheel', onWheel, { passive: false });
    return () => stage.removeEventListener('wheel', onWheel);
  }, [open]);

  /*
    The neighbours either side, and the one after next, fetched in the background with the same
    `srcset` and `sizes` the stage asks for, so the browser picks the same candidate and a step
    slides onto a photograph already in the cache rather than onto a request. Each is decoded as
    well as downloaded, and only then counted ready: a downloaded photograph still has to be
    decoded before it can be drawn, and decoding it mid-slide is the stutter this avoids.
  */
  const neighbours = useRef<string[]>([]);
  const fetched = useRef(new Set<string>());
  useEffect(() => {
    for (const set of neighbours.current) {
      if (!set || fetched.current.has(set) || ready.has(set)) continue;
      fetched.current.add(set);
      const image = new Image();
      image.sizes = '100vw';
      image.srcset = set;
      image.decode().then(
        () => markReady(set),
        // A failed preload is forgotten, so the step itself tries again rather than waiting on it.
        () => fetched.current.delete(set),
      );
    }
  });

  if (!viewer) return null;

  /*
    Two sources, two sequences. From a card the reader was scrolling the feed, so the next
    photograph is the next advertiser's — a second picture of the same person is not what they
    were moving through. From a gallery they were looking at one person's photographs, so the
    next is the next of those. Only the sequence differs; every gesture below is the same.
  */
  const feed = viewer.source === 'feed';
  const photos = feed ? [] : (data?.gallery ?? []);
  const count = feed ? listings.length : photos.length;
  // One photograph: the step row is still drawn, dimmed, with nowhere to step to.
  const single = count === 1;
  // Clamped before anything reads it, so a feed that shrank under an open viewer — a new search
  // behind the scrim — cannot address past its own end.
  const index = Math.min(viewer.index, Math.max(0, count - 1));
  const listing = feed ? listings[index] : undefined;
  const photo = feed ? undefined : photos[index];
  // Whose photograph this is, whichever sequence it came from. The feed knows it from the card it
  // is standing on; a gallery knows it from the profile it fetched.
  const name = feed ? listing?.working_name : data?.working_name;
  const advertiserId = feed ? listing?.advertiser_id : data?.advertiser_id;


  /** One variant's address for the photograph at position `at` of whichever sequence is open. */
  const urlAt = (at: number, variant: string) => {
    if (feed) {
      const images = listings[at]?.images;
      const found =
        variant === 'profile' ? images?.profile : variant === 'card-2x' ? images?.card_2x : images?.card;
      return found ? `${mediaBase}/${found}` : null;
    }
    const found = photos[at]?.variants.find((v) => v.variant === variant)?.key ?? null;
    return found && data ? `${data.media_base}/${found}` : null;
  };
  const url = (variant: string) => urlAt(index, variant);
  const srcSetAt = (at: number) =>
    [
      urlAt(at, 'card') ? `${urlAt(at, 'card')} 300w` : null,
      urlAt(at, 'card-2x') ? `${urlAt(at, 'card-2x')} 600w` : null,
      urlAt(at, 'profile') ? `${urlAt(at, 'profile')} 900w` : null,
    ]
      .filter(Boolean)
      .join(', ');
  // Wraps at both ends, because a viewer that stops dead on the last one reads as broken rather
  // than as finished.
  const wrap = (at: number) => (at + count) % count;
  neighbours.current =
    count > 1 ? [srcSetAt(wrap(index + 1)), srcSetAt(wrap(index - 1)), srcSetAt(wrap(index + 2))] : [];

  /** The incoming photograph's largest address, which is also what the stage will ask for. */
  const bestUrlAt = (at: number) => urlAt(at, 'profile') ?? urlAt(at, 'card-2x') ?? urlAt(at, 'card');
  /** Whose photograph sits at a position: the feed's own card, or the one profile a gallery is. */
  const nameAt = (at: number) => (feed ? listings[at]?.working_name : data?.working_name);
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const stageWidth = () => stageRef.current?.clientWidth || window.innerWidth;
  /**
   * How far a step that has to wait slides: the gap beside the incoming photograph's frame, plus
   * `PEEK` of the frame itself. The frame is a 3:4 photograph contained in the stage, so on a phone
   * it is the stage's width and on a desk it is a portrait column in the middle; measuring from the
   * frame rather than the stage is what brings the same share of it into view at every width.
   */
  const peekDistance = () => {
    const width = stageWidth();
    const height = stageRef.current?.clientHeight || window.innerHeight;
    const frame = Math.min(width, (height * 3) / 4);
    return (width - frame) / 2 + frame * PEEK;
  };

  /** Animate the track to `x`, from wherever the finger or the last animation left it. */
  function slideTo(x: number): Promise<void> {
    const track = trackRef.current;
    if (!track) return Promise.resolve();
    const from = offset.current;
    offset.current = x;
    const animation = track.animate(
      [{ transform: `translateX(${from}px)` }, { transform: `translateX(${x}px)` }],
      // Reduced motion lands every step at once; the placeholder still shows while it waits.
      { duration: reduced ? 0 : SLIDE_MS, easing: 'cubic-bezier(0.2, 0, 0, 1)', fill: 'forwards' },
    );
    return animation.finished.then(
      () => {
        track.style.transform = x ? `translateX(${x}px)` : '';
        animation.cancel();
      },
      () => undefined,
    );
  }

  /**
   * Finish a step: slide the incoming photograph the rest of the way, then make it the one on
   * screen. It is painted behind the stage for the moment the new element takes to draw it, so
   * the hand-over is invisible.
   */
  function complete(s: Slide) {
    if (slideRef.current !== s || s.phase === 'finishing' || s.phase === 'back') return;
    s.phase = 'finishing';
    void slideTo(-s.dir * stageWidth()).then(() => {
      if (slideRef.current !== s) return;
      setBehind(incomingRef.current?.currentSrc || null);
      offset.current = 0;
      if (trackRef.current) trackRef.current.style.transform = '';
      setSlide(null);
      setIndex(s.target);
    });
  }

  // A photograph that lands while a step is parked on its placeholder finishes that step.
  onReady.current = (set) => {
    const s = slideRef.current;
    if (s && s.set === set && s.phase === 'waiting') complete(s);
  };

  /**
   * The incoming photograph failed. It is not marked ready — the next visit tries again — but the
   * step is not left parked on a placeholder for something that will not come.
   */
  function giveUp(set: string) {
    const s = slideRef.current;
    if (!s || s.set !== set) return;
    s.set = '';
    if (s.phase === 'waiting') complete(s);
  }

  /** A step the finger did not carry far enough goes back where it came from. */
  function settle() {
    const s = slideRef.current;
    if (!s) return;
    s.phase = 'back';
    void slideTo(0).then(() => {
      if (slideRef.current === s) setSlide(null);
    });
  }

  /** The step for one direction, starting where a drag left off or from rest. */
  function slideFor(dir: 1 | -1, phase: Slide['phase']): Slide {
    const target = wrap(index + dir);
    return { dir, target, set: bestUrlAt(target) ? srcSetAt(target) : '', phase };
  }

  const move = (by: number) => {
    if (count < 2) return;
    const dir: 1 | -1 = by > 0 ? 1 : -1;
    const current = slideRef.current;
    // One step at a time: a press while one is already sliding is the same step, not a second.
    if (current && current.phase !== 'drag') return;
    const s = current && current.dir === dir ? current : slideFor(dir, 'going');
    s.phase = 'going';
    setSlide(s);
    if (!s.set || ready.has(s.set)) return complete(s);
    // Not downloaded yet: slide part of the way, onto a placeholder that says whose picture is
    // coming, and finish the moment it lands (`onReady`) — or straight away, if it landed while
    // the part-way slide was still moving.
    void slideTo(-dir * peekDistance()).then(() => {
      if (slideRef.current !== s || s.phase !== 'going') return;
      s.phase = 'waiting';
      if (!s.set || ready.has(s.set)) complete(s);
    });
  };

  // The pan is clamped to the photograph's own overhang, so no drag can push it off its own frame.
  function paint() {
    const el = imgRef.current;
    if (!el) return;
    const v = view.current;
    const overhangX = (el.clientWidth * (v.scale - 1)) / 2;
    const overhangY = (el.clientHeight * (v.scale - 1)) / 2;
    v.x = Math.max(-overhangX, Math.min(overhangX, v.x));
    v.y = Math.max(-overhangY, Math.min(overhangY, v.y));
    el.style.transform = v.scale === 1 ? '' : `translate(${v.x}px, ${v.y}px) scale(${v.scale})`;
  }

  /**
   * Zoom to `scale`, keeping the point under the fingers (or the cursor) where it is. Without a
   * focal point it zooms about the middle, which is what the keys mean.
   *
   * The photograph is centred on the stage and scaled about its own centre, so a point `p` on the
   * screen sits at `(p − centre − translate) / scale` on the photograph; holding that fixed across
   * the change of scale gives the new translation.
   */
  function zoomAt(scale: number, focalX?: number, focalY?: number) {
    const next = Math.max(1, Math.min(MAX_SCALE, scale));
    const v = view.current;
    const stage = stageRef.current?.getBoundingClientRect();
    if (next === 1) {
      v.x = 0;
      v.y = 0;
    } else if (stage && focalX !== undefined && focalY !== undefined) {
      const px = focalX - (stage.left + stage.width / 2);
      const py = focalY - (stage.top + stage.height / 2);
      const ratio = next / v.scale;
      v.x = px - (px - v.x) * ratio;
      v.y = py - (py - v.y) * ratio;
    }
    v.scale = next;
    paint();
    setZoomed(next > 1);
  }
  zoomRef.current = zoomAt;

  /** The distance between the first two fingers down, and the point midway, or null under two. */
  function pinch() {
    const [a, b] = [...pointers.current.values()];
    if (!a || !b) return null;
    return { span: Math.hypot(a.x - b.x, a.y - b.y), x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  }

  function onPointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    event.currentTarget.setPointerCapture(event.pointerId);
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    // A second finger is a pinch, and never the start of anything else.
    if (pointers.current.size === 2) {
      gesture.current = {
        startX: 0,
        startY: 0,
        lastX: 0,
        lastY: 0,
        panning: false,
        sliding: false,
        pinchFrom: pinch()?.span ?? 0,
        scaleFrom: view.current.scale,
      };
      return;
    }
    if (pointers.current.size > 2) return;

    gesture.current = {
      startX: event.clientX,
      startY: event.clientY,
      lastX: event.clientX,
      lastY: event.clientY,
      // The zoom decides what a drag is, at the moment the finger lands. A zoomed photograph is
      // being examined, so it moves under the finger straight away; there is nothing to wait for,
      // and waiting is what made it feel broken.
      panning: view.current.scale > 1,
      sliding: false,
      pinchFrom: 0,
      scaleFrom: view.current.scale,
    };
  }

  function onPointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    const tracked = pointers.current.get(event.pointerId);
    if (!tracked) return;
    tracked.x = event.clientX;
    tracked.y = event.clientY;

    const g = gesture.current;
    if (!g) return;

    if (g.pinchFrom > 0) {
      const now = pinch();
      if (now) zoomAt((g.scaleFrom * now.span) / g.pinchFrom, now.x, now.y);
      return;
    }

    if (g.panning) {
      view.current.x += event.clientX - g.lastX;
      view.current.y += event.clientY - g.lastY;
      g.lastX = event.clientX;
      g.lastY = event.clientY;
      paint();
      return;
    }

    g.lastX = event.clientX;
    g.lastY = event.clientY;

    // An unzoomed drag that is plainly sideways carries the photograph with the finger, and the
    // next one in from the side it is heading for. A step already sliding is left to finish.
    const dx = event.clientX - g.startX;
    const dy = event.clientY - g.startY;
    if (!g.sliding && count > 1 && !slideRef.current && Math.abs(dx) > TAP_PX && Math.abs(dx) > Math.abs(dy)) {
      g.sliding = true;
    }
    if (!g.sliding) return;
    const dir: 1 | -1 = dx < 0 ? 1 : -1;
    if (slideRef.current?.dir !== dir) setSlide(slideFor(dir, 'drag'));
    offset.current = dx;
    if (trackRef.current) trackRef.current.style.transform = `translateX(${dx}px)`;
  }

  function onPointerUp(event: ReactPointerEvent<HTMLDivElement>) {
    const g = gesture.current;
    pointers.current.delete(event.pointerId);
    if (!g) return;

    // Whichever finger lifts first ends the pinch, and the one still down is inert until it
    // lifts too: a pinch that decays into a swipe would move the photograph the reader was
    // still framing.
    if (g.pinchFrom > 0) {
      gesture.current = null;
      if (view.current.scale <= 1) zoomAt(1);
      track('interact', 'photo.viewer.zoom', { advertiser: advertiserId });
      return;
    }

    gesture.current = null;

    const travelled = event.clientX - g.startX;
    const drifted = event.clientY - g.startY;
    // A drag that carried the photograph: far enough is a step, which the slide finishes from
    // where the finger let go; short of that it springs back.
    if (g.sliding) {
      if (Math.abs(travelled) < SWIPE_PX) return settle();
      // A swipe is no click, so the delegated listener never hears it; it is counted here.
      track('interact', 'photo.viewer.swipe', { advertiser: advertiserId });
      move(travelled < 0 ? 1 : -1);
      return;
    }
    if (Math.abs(travelled) < TAP_PX && Math.abs(drifted) < TAP_PX) {
      // A tap on a zoomed photograph hands it back at its own size. Stepping on would throw away
      // the detail the reader zoomed in for, on a photograph they were still looking at.
      if (view.current.scale > 1) return void zoomAt(1);
      // Unzoomed, a tap says what a right-to-left swipe says: show me the next one.
      move(1);
      return;
    }
    // A drag on a zoomed photograph moved it, and moving it was the whole instruction.
    if (g.panning) return;
    if (Math.abs(travelled) < SWIPE_PX) return;
    // A swipe is no click, so the delegated listener never hears it; it is counted here.
    track('interact', 'photo.viewer.swipe', { advertiser: advertiserId });
    move(travelled < 0 ? 1 : -1);
  }

  function onPointerCancel(event: ReactPointerEvent<HTMLDivElement>) {
    pointers.current.delete(event.pointerId);
    if (gesture.current?.sliding) settle();
    gesture.current = null;
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    // A pointer device has no pinch, so the zoom has to be reachable from the keys as well —
    // which is also the only route for anyone who cannot make a two-finger gesture at all.
    if (event.key === '+' || event.key === '=') {
      event.preventDefault();
      zoomAt(view.current.scale * 1.5);
      track('interact', 'photo.viewer.zoom', { advertiser: advertiserId });
      return;
    }
    if (event.key === '-' || event.key === '_') {
      event.preventDefault();
      zoomAt(view.current.scale / 1.5);
      return;
    }
    if (event.key === '0') {
      event.preventDefault();
      zoomAt(1);
      return;
    }
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    track('interact', 'photo.viewer.key', { advertiser: advertiserId });
    move(event.key === 'ArrowRight' ? 1 : -1);
  }

  // The card's own image until the gallery lands, so the viewer opens on a photograph rather
  // than on a hole.
  const src = url('profile') ?? url('card-2x') ?? url('card') ?? viewer.poster ?? '';
  const srcSet = photo || listing ? srcSetAt(index) : undefined;


  /*
    Closing a feed viewer puts the reader back on the card whose photograph they ended on, which
    is not the card they opened it from — walking the feed is the whole point of the sequence.

    Focus has to move for that to mean anything. A dialog closing must put focus somewhere, and
    Radix's default is the control that opened it: thirty cards back, so the keyboard and the eye
    would end up on different advertisements. Sending both to the last card viewed is the only
    arrangement where they agree, and it is also what makes the next Tab continue from where the
    reader is rather than from where they started.

    `preventDefault` is what claims it. Radix composes this handler ahead of its own and skips
    that one on a prevented default, and the focus scope's own fallback to whatever was focused
    before is skipped by the same flag — so the one call replaces both, and returning early
    hands the whole arrangement back untouched.

    Focusing an element scrolls it into view on its own, and that scroll lands on the guessed
    geometry of a grid whose slots have not rendered. The settling loop runs afterwards and
    corrects it, exactly as it does on the way back from a profile.

    A gallery viewer does nothing here: there is no grid behind a profile. Neither does a feed
    viewer whose card has left the DOM. Both let Radix restore focus the way it wants to.
  */
  const onCloseAutoFocus = (event: Event) => {
    if (!feed || !listing) return;
    const card = document.querySelector<HTMLElement>(cardSelector(listing.listing_id));
    if (!card) return;
    event.preventDefault();
    card.querySelector<HTMLButtonElement>('.ee-tile__open')?.focus({ preventScroll: true });
    scrollCardIntoView(listing.listing_id);
  };

  return (
    <Dialog.Root open onOpenChange={(isOpen) => !isOpen && close()}>
      <Dialog.Portal>
        <Dialog.Overlay className="ee-scrim" />
        {/*
          The keys are handled here rather than on the stage. The stage is focusable, but Radix
          moves focus into the dialog when it opens and it does not land there — so the arrows
          were being pressed at an element that never heard them.
        */}
        <Dialog.Content
          className="ee-viewer"
          aria-describedby={undefined}
          onKeyDown={onKeyDown}
          onCloseAutoFocus={onCloseAutoFocus}
        >
          <Dialog.Title className="ee-visually-hidden">
            {name ? `${name}, ${index + 1} of ${count}` : 'Photograph'}
          </Dialog.Title>

          <Dialog.Close
            className="ee-viewer__close"
            aria-label="Close"
            data-ee-feature="photo.viewer.close"
          >
            <Icon name="close" />
          </Dialog.Close>

          {/*
            `touch-action: none` is what stops the browser claiming the horizontal drag for its
            own scroll gesture before the pointer handlers ever see it.
          */}
          <div
            ref={stageRef}
            className="ee-viewer__stage"
            role="group"
            tabIndex={0}
            aria-label="Photographs. Tap or swipe for the next one, or use the left and right arrow keys. Plus and minus zoom, and a zoomed photograph is dragged to move it."
            data-zoomed={zoomed ? 'true' : undefined}
            data-ee-feature="photo.viewer.advance"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
            style={behind && !slide ? { backgroundImage: `url("${behind}")` } : undefined}
          >
            <div ref={trackRef} className="ee-viewer__track">
            <div className="ee-viewer__pane">
            {src ? (
              <img
                // One element per photograph: a new one is what the dwell observer picks up, and
                // the one it replaces stays painted behind the stage until this one has decoded.
                key={photo?.id ?? listing?.listing_id ?? 'poster'}
                onLoad={() => {
                  setBehind(null);
                  // Already on screen, so already decoded: a step back to it slides straight in.
                  if (srcSet) markReady(srcSet);
                }}
                onError={() => setBehind(null)}
                ref={imgRef}
                className="ee-viewer__photo"
                src={src}
                srcSet={srcSet}
                sizes="100vw"
                width={900}
                height={1200}
                alt={name ?? ''}
                draggable={false}
                decoding="async"
                // Dwell counts against the advertiser and the photo, the same two keys the grid
                // and the gallery use, so one person is one key everywhere. Walking the feed
                // there is no photo id to file it under — a card carries the picture, not its
                // identity — so the advertiser carries the dwell on its own.
                data-ee-advertiser={advertiserId}
                data-ee-photo={photo?.id}
              />
            ) : null}
            </div>

            {/*
              The incoming photograph, beside the one on screen on the side it is coming from. It
              asks for the same `srcset` and `sizes` the stage will, so the element that replaces
              it draws from the cache. Until it has landed the placeholder stands in its frame,
              naming whose picture is being fetched; it carries no analytics keys, because it is
              not being looked at yet.
            */}
            {slide ? (
              <div
                className="ee-viewer__pane"
                style={{ transform: `translateX(${slide.dir * 100}%)` }}
              >
                {bestUrlAt(slide.target) ? (
                  <img
                    key={slide.set}
                    ref={incomingRef}
                    className="ee-viewer__photo"
                    src={bestUrlAt(slide.target) ?? ''}
                    srcSet={slide.set || undefined}
                    sizes="100vw"
                    width={900}
                    height={1200}
                    alt=""
                    draggable={false}
                    hidden={Boolean(slide.set) && !ready.has(slide.set)}
                    // Downloaded is not drawable: it is decoded before the slide is let finish.
                    onLoad={(event) => {
                      const set = slide.set;
                      void event.currentTarget
                        .decode()
                        .catch(() => undefined)
                        .then(() => markReady(set));
                    }}
                    // A photograph that will not load is not waited on: the step finishes and the
                    // stage shows what it can.
                    onError={() => giveUp(slide.set)}
                  />
                ) : null}
                {slide.set && !ready.has(slide.set) ? (
                  <div className="ee-viewer__skeleton">
                    {/*
                      Pinned to the frame's edge the photograph comes in from, and as wide as the
                      share of the frame a waiting step brings into view, so the words are whole
                      on screen while it waits, at any width.
                    */}
                    <p
                      className="ee-viewer__skeleton-label"
                      role="status"
                      data-from={slide.dir === 1 ? 'right' : 'left'}
                      style={{ width: `${PEEK * 100}%` }}
                    >
                      {nameAt(slide.target)
                        ? `Fetching picture of ${nameAt(slide.target)}`
                        : 'Fetching picture'}
                    </p>
                  </div>
                ) : null}
              </div>
            ) : null}
            </div>
          </div>

          {/*
            Opened from a Gallery tile, the viewer wears that tile's plate: who, where, how old.
            A profile's own viewer has none — there the facts are on the page behind. It is
            `pointer-events: none`, and it steps aside for a zoomed photograph so it never
            covers the detail the reader pinched in for.
          */}
          {listing && !zoomed ? (
            <div className="ee-viewer__plate">
              <Link
                className="ee-viewer__who"
                to={`/p/${listing.username}`}
                onClick={(event) => openProfile(event, listing)}
                data-ee-feature="photo.viewer.profile"
              >
                <span className="ee-viewer__name">{listing.working_name}</span>
                <span className="ee-viewer__handle">{listing.handle}@EnE</span>
                <span className="ee-viewer__place">{listingWhereAge(listing)}</span>
                <span className="ee-visually-hidden">, view profile</span>
              </Link>
            </div>
          ) : null}

          {/* The position, said aloud: the title changing is not announced, and the count is hidden. */}
          <p className="ee-visually-hidden" aria-live="polite">
            {count > 0 ? `Photo ${index + 1} of ${count}${feed && name ? `, ${name}` : ''}` : ''}
          </p>

          {failed ? (
            <div className="ee-viewer__failed" role="alert">
              <p>Couldn't load the photos.</p>
              <button
                type="button"
                className="ee-viewer__retry"
                onClick={() => setAttempt((n) => n + 1)}
                data-ee-feature="photo.viewer.retry"
              >
                Try again
              </button>
            </div>
          ) : null}

          {/* Drawn whenever there is a photograph, one included: a single photograph shows the
              same row in the same place, dimmed, so the controls never appear and vanish between
              advertisers. `aria-disabled` rather than `disabled`, so the buttons stay in the tab
              order and a screen reader says why nothing happens; `move` already ignores a step
              with nowhere to go. */}
          {count > 0 ? (
            <div className="ee-viewer__nav" data-single={single ? '' : undefined}>
              <button
                type="button"
                className="ee-viewer__step"
                aria-label="Previous photo"
                aria-disabled={single || undefined}
                onClick={() => move(-1)}
                data-ee-feature="photo.viewer.prev"
              >
                <Icon name="chevron-left" />
              </button>
              <span className="ee-viewer__count" aria-hidden="true">
                {index + 1} / {count}
              </span>
              <button
                type="button"
                className="ee-viewer__step"
                aria-label="Next photo"
                aria-disabled={single || undefined}
                onClick={() => move(1)}
                data-ee-feature="photo.viewer.next"
              >
                <Icon name="chevron-right" />
              </button>
            </div>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
